<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import {
  BModal, BButton, BAlert, BRow, BCol, BFormGroup, BFormCheckbox, AlertPlugin, BFormInput, BTable,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import { required, email } from '@validations';
import axios from '@/libs/axios';
import ItemModal from '@/views/app/operacoes/picitem/list/ModalBuscarItens.vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import PicFaixasEditor from './PicFaixasEditor.vue';
import { normalizarFaixas, precoPorFaixa } from '@/services/Operacoes/picFaixas';

export default {
  components: {
    PicFaixasEditor,
    BFormInput,
    BModal,
    BButton,
    BAlert,
    vSelect,
    ItemModal,
    BRow,
    BCol,
    BFormGroup,
    BFormCheckbox,
    BTable,
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    picid: {
      type: Number,
      default: null,
      require: false,
    },
    tabelaprecoid: {
      type: [String, Number],
      default: null,
      require: false,
    },
    titleModal: {
      type: String,
      default: null,
      require: false,
    },
    idModal: {
      type: String,
      default: null,
      require: false,
    },
    labelBtnOk: {
      type: String,
      default: null,
      require: false,
    },
  },
  data() {
    return {
      dataFromApiitem: null,
      quantity: '',
      discount: '',
      valor: '',
      visibleValor: false,
      isLoading: false,
      porFaixa: false,
      faixas: [],
      erro: '',
      precosBase: {},
      quoteStatus: 'idle',
      quoteGeneration: 0,
    };
  },
  computed: {
    itemIds() {
      return (this.dataFromApiitem || []).map(item => item.value ?? item.itemid ?? item.item_id)
        .map(value => String(value ?? '').trim())
        .filter(Boolean);
    },
    selectedTableId() {
      return this.tabelaprecoid || null;
    },
    quoteFailed() {
      return this.quoteStatus === 'failed';
    },
    quotePending() {
      return this.quoteStatus === 'pending';
    },
    quoteReady() {
      return this.quoteStatus === 'ready'
        && this.itemIds.length > 0
        && this.itemIds.every(itemid => Number.isFinite(Number(this.precosBase[itemid]))
          && Number(this.precosBase[itemid]) > 0);
    },
    priceRows() {
      return (this.dataFromApiitem || []).map(item => {
        const itemid = String(item.value ?? item.itemid ?? item.item_id);
        return {
          ...item,
          itemid,
          precoBase: this.precosBase[itemid],
        };
      });
    },
    basePriceForFaixas() {
      return this.dataFromApiitem?.length === 1
        ? this.basePriceFor(this.dataFromApiitem[0])
        : null;
    },
    disableDiscount() {
      return this.porFaixa;
    },
    disableValor() {
      return true;
    },
    dataForm: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  methods: {
    async validateForm(event) {
      if (event) event.preventDefault();
      if (this.isLoading) return;
      if (this.quoteStatus && this.quoteStatus !== 'ready') return;
      this.isLoading = true;
      this.erro = '';
      try {
        if (!this.dataFromApiitem?.length) throw new Error('Selecione um produto.');
        if (!Number.isSafeInteger(Number(this.quantity)) || Number(this.quantity) < 1) {
          throw new Error('Informe a quantidade disponibilizada, maior que zero.');
        }
         const faixas = this.porFaixa ? normalizarFaixas(this.faixas) : [];
        if (this.porFaixa) {
          if (!faixas.length) throw new Error('Adicione pelo menos uma faixa.');
           if (!Number.isFinite(this.basePriceForFaixas) || this.basePriceForFaixas <= 0) {
             throw new Error('Preço-base não encontrado para o produto selecionado.');
           }
           faixas.forEach(row => precoPorFaixa(this.basePriceForFaixas, faixas, row.min));
        }
        try {
          const { data } = await axios.get('/pic-item/faixas/capabilities');
          if (data?.quantityTiers !== true) throw new Error();
        } catch (_) {
          throw new Error('O servidor ainda não está pronto para salvar faixas. A atualização do backend precisa ser concluída; nenhum item foi enviado.');
        }
         // Keep the quote attached to each SKU.  In particular, never use the
         // first selected product's price as the base for all the others.
         const items = this.priceRows.map(item => ({
           ...item,
           picitemprecobase: Number(item.precoBase),
           picitemvalorpic: this.precoPromocional(item.precoBase),
         }));
         const payload = {
           picid: this.picid,
           items,
           picitemquantidade: this.quantity,
           picitemquantidadesaldo: this.quantity,
           // Legacy servers read this root value.  It is safe only for one
           // product; the item-level values above are authoritative for a
           // multiselect request.
           picitemvalorpic: items.length === 1 ? items[0].picitemvalorpic : undefined,
           picitemporcentagem: this.porFaixa ? 0 : this.discount,
           picitemfaixas: faixas,
           picitemsituacao: 'Pendente',
           picitemtotalmercadoria: 0,
         };
         if (this.selectedTableId) payload.tabelaprecoid = this.selectedTableId;
        await axios.post('/pic-item', {
           ...payload,
         });
        this.$bvModal.hide(this.idModal);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso',
            text: 'Item cadastrado com sucesso!',
            icon: 'CheckIcon',
            variant: 'success',
          },
        });

        this.$emit('ok');
      } catch (error) {
        this.erro = error.response?.data?.message || error.message || 'Não foi possível salvar o item.';
        this.isLoading = false;
      } finally {
        this.isLoading = false;
      }
    },

    showModal(items) {
      this.quoteGeneration += 1;
      this.dataFromApiitem = items;
      this.visibleValor = false;
      this.quantity = '';
      this.valor = '';
      this.discount = '';
      this.porFaixa = false;
      this.faixas = [];
      this.erro = '';
      this.precosBase = {};
      this.quoteStatus = 'pending';
      this.$refs.quantityDiscountModal.show();
      this.fetchPrecosBase(items);
    },
    async fetchPrecosBase(items) {
      const generation = this.quoteGeneration;
      const selectedItems = Array.isArray(items) ? items.slice() : [];
      const itemids = selectedItems.map(item => item.value ?? item.itemid ?? item.item_id)
        .map(value => String(value ?? '').trim())
        .filter(Boolean);
      if (!itemids.length) {
        this.quoteStatus = 'failed';
        this.erro = 'Selecione pelo menos um produto para consultar o preço-base.';
        return;
      }
      try {
        const body = { picid: this.picid, itemids };
        if (this.selectedTableId) body.tabelaprecoid = String(this.selectedTableId);
        const response = await axios.post('/pic-item/precos', body);
        if (generation !== this.quoteGeneration) return;
        const rows = response.data?.data || response.data?.precos || response.data || [];
        const byItem = rows.reduce((acc, row) => {
          const itemid = row?.itemid ?? row?.item_id ?? row?.itemId;
          const price = row?.precoBase ?? row?.preco_base ?? row?.preco ?? row?.item_price;
          if (itemid != null) acc[String(itemid)] = Number(price);
          return acc;
        }, {});
        const missing = itemids.filter(itemid => !Number.isFinite(byItem[itemid]) || byItem[itemid] <= 0);
        if (missing.length) {
          const descriptions = selectedItems
            .filter(item => missing.includes(String(item.value ?? item.itemid ?? item.item_id)))
            .map(item => item.text || item.value)
            .join(', ');
          throw new Error(`Preço-base não encontrado para: ${descriptions || missing.join(', ')}.`);
        }
        this.precosBase = byItem;
        this.valor = this.basePriceForFaixas || '';
        this.quoteStatus = 'ready';
      } catch (error) {
        if (generation !== this.quoteGeneration) return;
        this.precosBase = {};
        this.quoteStatus = 'failed';
        this.erro = error.response?.data?.message || error.message
          || 'Não foi possível consultar os preços-base dos produtos.';
      }
    },
    precoPromocional(precoBase) {
      const base = Number(precoBase);
      const desconto = Number(this.discount || 0);
      return Number((base * (1 - desconto / 100)).toFixed(4));
    },
    invalidateQuote() {
      this.quoteGeneration += 1;
      this.quoteStatus = 'idle';
    },
    changePricingMode() {
     if (this.porFaixa && !this.faixas.length) this.faixas = [{ min: 1, max: null, desconto: 0 }];
    },
    basePriceFor(item) {
      const itemid = typeof item === 'string'
        ? item
        : String(item?.value ?? item?.itemid ?? item?.item_id);
      return Number(this.precosBase[itemid]);
    },
  },
};
</script>

<template>
  <b-modal no-close-on-backdrop no-close-on-esc scrollable ref="quantityDiscountModal" size="lg" :id="idModal" :title="titleModal"
     :ok-disabled="isLoading || quotePending || quoteFailed || !quoteReady" :cancel-disabled="isLoading"
    :ok-title="labelBtnOk" ok-variant="success" cancel-variant="secondary" body-bg-variant="light"
     cancel-title="Cancelar" @ok="validateForm" @cancel="$emit('cancel'); invalidateQuote()" @close="$emit('cancel'); invalidateQuote()">
    <validation-observer ref="refFormData">

      <b-form @submit.prevent>
         <b-alert :show="!!erro" variant="danger">{{ erro }}</b-alert>
         <b-alert v-if="quotePending" show variant="info">
           Consultando o preço-base de cada produto selecionado...
         </b-alert>
        <p class="mb-1">Configure o preço e as faixas para os produtos selecionados. A quantidade disponibilizada é o limite de uso da promoção, não uma faixa de desconto.</p>
         <b-table v-if="priceRows.length" :items="priceRows" :fields="[{ key: 'text', label: 'Produto' }, { key: 'precoBase', label: 'Preço-base (R$)' }]" small bordered class="mb-1">
           <template #cell(precoBase)="row">
             <span v-if="Number.isFinite(Number(row.item.precoBase))">
               {{ Number(row.item.precoBase).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) }}
             </span>
             <span v-else class="text-danger">Não encontrado</span>
           </template>
         </b-table>
        <b-row>
          <b-form-group class="col-12" label="Quantidade disponibilizada por produto" label-for="input-quantidade">
            <b-form-input id="input-quantidade" v-model="quantity" type="number" min="1" required></b-form-input>
          </b-form-group>
          <b-form-group class="col-12" label="Como aplicar o desconto?">
            <label class="mr-2"><input v-model="porFaixa" type="radio" :value="false" @change="changePricingMode"> Preço ou desconto fixo</label>
            <label><input v-model="porFaixa" type="radio" :value="true" @change="changePricingMode"> Por faixa de quantidade</label>
          </b-form-group>
           <b-form-group class="col-12" :label="porFaixa ? 'Preço-base por unidade (R$), antes do desconto' : 'Preço-base por unidade (R$)'" label-for="input-valor">
             <b-form-input id="input-valor" :value="basePriceForFaixas || 'Consulte os preços acima'" readonly disabled></b-form-input>
          </b-form-group>
          <b-form-group v-if="!porFaixa" class="col-12" label="Desconto fixo (%)" label-for="input-desconto">
            <b-form-input id="input-desconto" :disabled="disableDiscount" v-model="discount" type="number" min="0"
              max="100" step="0.01" required></b-form-input>
          </b-form-group>
        </b-row>
         <PicFaixasEditor v-if="porFaixa" v-model="faixas" :base="basePriceForFaixas" />
      </b-form>
    </validation-observer>
  </b-modal>
</template>
